import type { IValues } from '../../../containers/NewLocation/valuesTypes';

import classNames from 'classnames';

import { useState, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { screenedValues } from '../../../tools/constants';
import { useCountries } from '../../../hooks/useCountries';
import { useStates } from '../../../hooks/useStates';
import { useTranslation } from 'react-i18next';


export const LocationInformation = ({
    handler,
    editable = true,
    single,
}: {
    handler?: (step: number) => void;
    editable?: boolean;
    single?: boolean;
}) => {
    const { t } = useTranslation();
    const { values, errors, touched, submitCount } = useFormikContext<IValues>();

    const countriesQuery = useCountries('default' !== values.country);
    const statesQuery = useStates('default' !== values.country ? values.country : undefined);

    const statesData = useMemo(
        () => (statesQuery.data?.data.Data || []).find(state => state.ProvinceId === values.state),
        [statesQuery.data?.data.Data, values.state]
    );
    const countriesData = useMemo(
        () => (countriesQuery.data?.data.Data || []).find(country => country.CountryId === values.country),
        [countriesQuery.data?.data.Data, values.country]
    );

    const [isExpanded, setIsExpanded] = useState(single);

    const isFormCompleted = useMemo(
        () => screenedValues[0].every(field => !errors[field] && touched[field]),
        [errors, touched]
    );

    return (
        <div
            className={classNames('cco_collapse', {
                active: isExpanded,
            })}
        >
            <div className="cco_collapse_header">
                <div className="cco_text cco_text_3">
                    <div className="cco_text_inner">
                        <h3>1. {t("NewLocation.LocationInformation.LocationInformation")} </h3>

                        <div
                            className={classNames({
                                success: isFormCompleted,
                                error: !isFormCompleted,
                            })}
                        >
                            {isFormCompleted? t("NewLocation.LocationInformation.Completed"): submitCount < 1? t("NewLocation.LocationInformation.InProgress") : t("NewLocation.LocationInformation.fill")}
                        </div>
                    </div>
                </div>

                <button
                    type="button"
                    className="cco_collapse_toggle"
                    onClick={() => setIsExpanded(prevState => !prevState)}
                >
                    <span className="cco_collapse_toggle_text">{t("NewLocation.LocationInformation.View")}</span>
                </button>
            </div>

            <div className="cco_collapse_body">
                <div className="cco_collapse_body_inner">
                    <div className="cco_blurb cco_blurb_1">
                        <div className="cco_blurb_label">{t("NewLocation.LocationInformation.CompanyName")}</div>
                        <div className="cco_blurb_value">
                            <strong>{values.companyName ||  t("NewLocation.LocationInformation.Notcompleted")}</strong>
                        </div>
                    </div>

                    <div className="cco_blurb cco_blurb_1">
                        <div className="cco_blurb_label">{t("NewLocation.LocationInformation.Street1")}</div>
                        <div className="cco_blurb_value">
                            <strong>{values.street1 ||  t("NewLocation.LocationInformation.Notcompleted")}</strong>
                        </div>
                    </div>

                    <div className="cco_blurb cco_blurb_1">
                        <div className="cco_blurb_label">{t("NewLocation.LocationInformation.Street2")}</div>
                        <div className="cco_blurb_value">
                            <strong>{values.street2 ||  t("NewLocation.LocationInformation.Notcompleted")}</strong>
                        </div>
                    </div>

                    <div className="cco_blurb cco_blurb_1">
                        <div className="cco_blurb_label">{t("NewLocation.LocationInformation.City")}</div>
                        <div className="cco_blurb_value">
                            <strong>{values.city ||  t("NewLocation.LocationInformation.Notcompleted")}</strong>
                        </div>
                    </div>

                    <div className="cco_blurb cco_blurb_1">
                        <div className="cco_blurb_label">{t("NewLocation.LocationInformation.Zip")}</div>
                        <div className="cco_blurb_value">
                            <strong>{values.zip ||  t("NewLocation.LocationInformation.Notcompleted")}</strong>
                        </div>
                    </div>

                    <div className="cco_blurb cco_blurb_1">
                        <div className="cco_blurb_label">{t("NewLocation.LocationInformation.State")}</div>
                        <div className="cco_blurb_value">
                            <strong>
                                {statesQuery.data ? statesData?.ProvinceName ||  t("NewLocation.LocationInformation.Notcompleted") : t("NewLocation.LocationInformation.waiting")}
                                
                            </strong>
                        </div>
                    </div>

                    <div className="cco_blurb cco_blurb_1">
                        <div className="cco_blurb_label">{t("NewLocation.LocationInformation.Country")}</div>
                        <div className="cco_blurb_value">
                            <strong>
                                {countriesQuery.data ? countriesData?.CountryName ||  t("NewLocation.LocationInformation.Notcompleted") : t("NewLocation.LocationInformation.waiting")}
                            </strong>
                        </div>
                    </div>

                    <div className="cco_blurb cco_blurb_1">
                        <div className="cco_blurb_label">{t("NewLocation.LocationInformation.Securefacility")}</div>
                        <div className="cco_blurb_value">
                            <strong>
                                {'default' !== values.isASecureFacility
                                    ? 'yes' === values.isASecureFacility
                                        ? 'Yes'
                                        : 'No'
                                    : t("NewLocation.LocationInformation.Notcompleted")}
                            </strong>
                        </div>
                    </div>

                    {'yes' === values.isASecureFacility && (
                        <div className="cco_blurb cco_blurb_1">
                            <div className="cco_blurb_label">{t("NewLocation.LocationInformation.Additionalsecurity")}</div>
                            <div className="cco_blurb_value">
                                <strong>{values.additionalSecurity ? t("NewLocation.LocationInformation.Completed") : t("NewLocation.LocationInformation.Notcompleted")}</strong>
                            </div>
                        </div>
                    )}

                    {editable && (
                        <div className="cco_btn cco_btn_2">
                            <a
                                href="#"
                                onClick={e => {
                                    handler?.(0);
                                    e.preventDefault();
                                }}
                            >
                                {t("NewLocation.LocationInformation.Edit")}
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
